import { render } from 'react-dom'
import './styles.css'
import * as THREE from 'three'
import { useRef, Suspense, useEffect } from 'react'
import { Canvas, useFrame, useThree, extend } from '@react-three/fiber'
import { Center, Bounds, Instances, Instance } from '@react-three/drei'
import * as Tone from 'tone'
import * as f from 'fractal-noise'
import * as f2 from 'fast-simplex-noise'
import Colors from './Colors'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass'

function shuffle(array) {
  let currentIndex = array.length, randomIndex;
  while (currentIndex != 0) {
    randomIndex = Math.floor(fxrand() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }
  return array;
}

let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
let lerp = THREE.MathUtils.lerp
const ci = randI(Colors.length)
const colors = Colors[ci]
const bgColor = colors.shift()
const cols = colors
shuffle(cols)

let columns = 1200;
const feats = window.$fxhashFeatures = {
  phases: 2 + randI(3),
  colMod: 1 + randI(100),
  rowMod: 1 + randI(20),
  colScaleX: 1 + randI(2),
  colScaleY: 1 + randI(20),
  lerpSpeed: (1 + randI(10)) * 0.005 * (isFxpreview ? 2 : 1),
  freqUnit: rand([0.001, 0.0001, 0.0001]),
  xStep: 1 + randI(50),
  yStep: 1 + randI(50),
  colorMod: 1 + randI(15),
  border: rand([0,1,2, 3,3,3,4, 5, 7]),
  paintSteps: 60 + randI(200),
  bpm: rand([60, 70, 80, 90, 100]) * (isFxpreview ? 0.125 : 1),
  opacity: rand([0.9, 0.8, 0.7]),
  phaseNotes: 4 + randI(12),
}
// console.table(feats)
let rect = f.makeRectangle(columns, 1, f2.makeNoise2D(fxrand), { frequency: (1 + randI(25)) * (feats.freqUnit), octaves: 1 + randI(12), persistence: randI(10)*0.1 })

class AfterBurn extends AfterimagePass {
  constructor() {
    super(1)
    this.shaderMaterial.onBeforeCompile = (shader) => {
      shader.fragmentShader =
        `
        uniform float damp;
        uniform sampler2D tOld;
        uniform sampler2D tNew;
        varying vec2 vUv;
    
        vec4 when_gt( vec4 x, float y ) {
          return max( sign( x - y ), 0.0 );
        }

        void main() {
          vec4 texelOld = texture2D( tOld, vUv );
          vec4 texelNew = texture2D( tNew, vUv );
          texelOld *= damp * when_gt( texelOld, 0.001 );
          gl_FragColor = max(texelNew, texelOld);
        }
        `
    }
  }
}

extend({ EffectComposer, RenderPass, AfterBurn })

const Tint = () => {
  const { size, gl, scene, camera } = useThree()
  const composer = useRef()

  useFrame(() => composer.current.render(), 1)
  return (
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachFns={['addPass']} args={[scene, camera]} />
      <afterBurn attachFns={['addPass']} args={[size]} />
    </effectComposer>
  )
}

export default function App() {
  const start = () => {
    if (Tone.Transport.state !== 'started') {
      Tone.start();
      Tone.Transport.start();
      parts.forEach((p, i) => { p.start() })
    }
  }
  isFxpreview && start()
  return (
    <div id="main" onClick={() => start()}>
      <Canvas flat={true} dpr={[1, 2]} camera={{ position: [0.2, 2, -3], fov: 27 }}>
        <group position={[0, 0, 0]}>
          <Stuff />
        </group>
        <Back />
        <Tint />
        <ambientLight />
      </Canvas>
    </div>
  )
}

let s = 0.001 * (isFxpreview ? 1.5 : 1)
let geo = new THREE.BoxBufferGeometry(s, s, s)
let opacity = 0

function Stuff() {
  const mat = useRef()
  useFrame(() => {
    mat.current.opacity = opacity
  })
  return (
    <Bounds fit margin={0.4} damping={0}>
      <Center>
        <Instances
          limit={10000}
          range={10000}
          geometry={geo}
        >
          <meshStandardMaterial ref={mat} transparent opacity={0} />
          {new Array(columns).fill().map((_, col) => new Array(1).fill().map((_, row) => <Dot col={col} row={row} key={row + col} />))}
        </Instances>
      </Center>
    </Bounds>
  )
}
let xCount = 0;
let yScale = (1+randI(6))*0.1
function Dot({ col, row }) {
  const ref = useRef();
  let s = 0.002
  let getY = () => rect[col][0] * yScale
  let getZ = () => 0.6 - rowCount * (s * 0.4)
  let getX = () => (xCount * s) + (col * s)
  useFrame(() => {
    let onCol = (col % feats.colMod) === 0
    let onRow = (rowCount % feats.rowMod) === 0
    ref.current.color.copy(noteCol)
    ref.current.position.x = lerp(ref.current.position.x, getX(), feats.lerpSpeed)
    ref.current.position.z = lerp(ref.current.position.z, getZ(), feats.lerpSpeed)
    ref.current.position.y = lerp(ref.current.position.y, -0.4 + getY(0) + (horiCount * (s * 0.4)), 0.8)
    ref.current.scale.x = ((onCol || onRow) ? feats.colScaleX : 1)
    ref.current.scale.y = ((onCol || onRow) ? feats.colScaleY : 1)
  })
  return (
    <Instance ref={ref} position={[getX(), -0.4 + getY(0), getZ()]} />
  )
}
function Back({ col, row }) {
  const ref = useRef();
  useFrame((state) => {
    if (state.clock.getElapsedTime() > 1) {
      ref.current.material.visible = false

    }
  })
  useEffect(() => {
    setTimeout(() => {
      document.getElementById('borders').style.display = 'unset'
      opacity = feats.opacity
    }, 500)
  }, [])
  return (
    <mesh ref={ref}>
      <meshBasicMaterial color={bgColor} side={THREE.BackSide} />
      <sphereBufferGeometry args={[6, 6, 6]} />
    </mesh>
  )
}

Tone.Transport.bpm.value = feats.bpm
var pianoNotesPool = ["C", "Ds", "Fs"];
let pianoSamples = {};
pianoNotesPool.map((val) => {
  for (var i = 3; i < 7; i++)
    pianoSamples[val.replace(/s/gi, '#') + i] = val + i + 'v' + rand([3,1, 1, 1, 1, 1]) + '.[mp3|ogg]';
})
var pianoParams = [
  pianoSamples,
  {
    "release": 2,
    "attack": 0.1,
    "baseUrl": "./salamanderlight/"
  }]

let sampler = new Tone.Sampler(...pianoParams).toDestination()
let durs = ['1m', '2n', '2m', '2t', '1t', '4t']


let parts = []
let maxRows = rand([100])
let maxHori = 600
let rowCount = randI(maxRows);
let horiCount = -1
let noteCol = new THREE.Color(cols[0])
let dir = rand([true, false])
let absCount = 0;
let previewed = false

new Array(feats.phases).fill().map((_, j) => {
  let time = 0;
  let dur = 0;
  let phase = new Array(feats.phaseNotes).fill().map((_, i) => {
      let dur = Tone.Time(rand(durs)).toSeconds()
      let note = rand(['C', 'D', 'E', 'F']) + rand([3, 4, 5, 6])
      let vel = 0.03 + (randI(4) * 0.01)
      let x = dur * feats.xStep * rand([1, -1])
      let y = dur * feats.yStep * rand([1, -1])
      let z = (randI(100) > 98 ? (randI(5) * 10 * rand([1, -1])) : 0) * dur
      let res = {
        time,
        dur,
        i,
        j,
        note,
        x,
        y,
        z,
        vel,
      }
      time = + dur
      return res
    })

  let pCount = 0
  let part = new Tone.Part(((time, { note, i, j, dur, x, y, z, vel }) => {
    pCount += 1

    sampler.triggerAttackRelease(note.replace(/s/gi, '#'), dur, time, vel);
    noteCol.set(cols[Math.floor((pCount / feats.colorMod)) % cols.length])

    if (rowCount > maxRows || -rowCount > maxRows || rowCount === 0) {
      rowCount = (rowCount > maxRows) ? 0 : maxRows
    }
    if (horiCount > maxHori || -horiCount > maxHori || horiCount === 0) {
      horiCount = (horiCount > maxHori) ? -maxHori : maxHori
    }
    dir = rowCount > maxRows || rowCount < -maxRows
    absCount += (Math.abs(y) + Math.abs(x))
    if (absCount < maxRows * feats.paintSteps) {
      horiCount += x
      rowCount += y * (dir ? 1 : -1)
      xCount += z
    }else if(!previewed) {
      fxpreview()
      previewed = true
    }
  }), phase)


  part.loop = true
  part.loopEnd = time + dur + (randI(8) * 0.0004 * rand([1, -1]))
  parts.push(part)
})
console.log('%c🎹🌊 @ramioso 2022-04-22 🌊🎹', 'color: orange');
let b = feats.border +'wv'
render(
  <>
    <div id="borders">
      <div style={{ background: bgColor, width: b, height: b }} />
      <div style={{ background: bgColor, width: b, height: b }} />
      <div style={{ background: bgColor, width: b, height: b }} />
      <div style={{ background: bgColor, width: b, height: b }} />
      <div style={{ background: bgColor, width: b, height: b }} />
    </div>
    <Suspense fallback={null}>
      <App />
    </Suspense>
  </>,
  document.getElementById('root')
)